// Pure model of the session the Vue shell opens (src/utils/auth/sessionService.js), ported from the legacy
// login and portal pages (decommissioned/m.eagle/app/pages/login, pages/home) and their translators
// (public/m.eagle/app/models/core/user/userTranslator.js, app/appTranslator.js). The objects it builds are
// the ones the legacy stores and reads (ctx.user, externis.applications): keep their shapes.

// public/m.eagle/app/models/core/user/userModel.js
export const emptyUser = () => ({
	id: 0,
	pass: '',
	userName: '',
	firstName: '',
	lastName: '',
	email: '',
	roleId: 0,
	roleSalesId: 0,
	roleDocId: 0,
	roleAssortId: 0,
	role: '',
	roleSales: '',
	roleDoc: '',
	roleAssort: '',
	customer: '',
	customerCode: '',
	token: '',
	tokenSales: null,
	tokenAssort: null,
	tokenDoc: null,
	locale: '',
	timezone: '',
	organisationId: 0,
	organisationLabel: '',
	organisationLevel: 999,
	isAdmin: false,
	isDelegate: false,
	lastLogonDate: '',
	deliveryLocationHomeId: 0,
	roles: [],
	accounts: [],
	productTree: [],
	categories: [],
	processTypes: [],
	settings: [],
	customerLoginCode: '',
	userLastLoginCheck: 0,
});

export const DEFAULT_LOCALE = 'fr-FR';

// userTranslator.fromJson(): the Auth/Logon (or LogonApplication) answer.
export function userFromLogon(o) {
	const user = emptyUser();
	if (!o) return user;
	return Object.assign(user, {
		id: o.id,
		token: o.token,
		userName: o.username,
		firstName: o.firstName || '',
		lastName: o.lastName || '',
		email: o.email,
		roleId: o.roleId,
		role: o.role,
		customer: o.customer,
		customerCode: o.customerCode,
		locale: o.locale || DEFAULT_LOCALE,
		timezone: o.timezone,
		organisationId: o.organisationId,
		organisationLabel: o.organisationLabel,
		lastLogonDate: o.lastLogonDate ? new Date(o.lastLogonDate).toISOString() : '',
		isAdmin: o.isAdmin,
		isDelegate: o.isDelegate,
		deliveryLocationHomeId: o.deliveryLocationHomeId,
		roles: o.roles,
		accounts: o.accounts,
		productTree: o.productTree,
		categories: o.categories,
		processTypes: o.processTypes,
	});
}

// The application logons whose tokens the legacy keeps on ctx.user (eagle.js logUser, userTranslator.fromApp()).
export const APPLICATION_TOKENS = {
	SALFO: { token: 'tokenSales', roleId: 'roleSalesId', role: 'roleSales' },
	ASSFO: { token: 'tokenAssort', roleId: 'roleAssortId', role: 'roleAssort' },
	DCRFO: { token: 'tokenDoc', roleId: 'roleDocId', role: 'roleDoc' },
};

// userTranslator.fromApp(): only the attributes of one application.
export function withApplicationLogon(user, o, application) {
	const keys = APPLICATION_TOKENS[application];
	const next = { ...user };
	if (keys && o) Object.assign(next, { [keys.token]: o.token, [keys.roleId]: o.roleId, [keys.role]: o.role });
	if (o) next.lastLogonDate = o.lastLogonDate ?? next.lastLogonDate;
	if (!next.locale) next.locale = DEFAULT_LOCALE;
	return next;
}

// userTranslator.copyTokens() (login.js keeps the application tokens and roles of the stored user).
export function copyApplicationTokens(dest, src) {
	const next = { ...dest };
	if (src) Object.values(APPLICATION_TOKENS).forEach((keys) => Object.values(keys).forEach((key) => (next[key] = src[key])));
	if (!next.locale) next.locale = DEFAULT_LOCALE;
	return next;
}

const optionValue = (options, name) =>
	String((Array.isArray(options) ? options : []).find((o) => o?.name === name && o?.application === 'SALFO')?.value ?? '')
		.toLowerCase()
		.trim();

// salesHelper.checkWebAppActive(): the web application is enabled for the customer.
export const isWebAppActive = (options) => optionValue(options, 'WebAppSalesFO') === 'true';

// salesHelper.isSFAActivated(): sets externis.sfaActive, which gates the whole SFA synchronization (eagle.js).
export const isSfaActive = (options, processTypes) => optionValue(options, 'SFA') === 'full' && Array.isArray(processTypes) && processTypes.includes(10);

const decode = (value) => {
	try {
		return atob(value);
	} catch {
		return '';
	}
};

// home.js loadSLSettingOffline(): base64 customer lists of sl.json, 'ALL' for everyone.
export function isCustomerListed(list, customer) {
	if (!Array.isArray(list) || !list.length) return false;
	const customers = list.map(decode);
	return customers.includes('ALL') || customers.includes(customer);
}

// What the legacy portal set on externis before the application could run.
export function sessionFlags({ salesOptions, settings, user }) {
	return {
		sfaActive: isSfaActive(salesOptions, user?.processTypes),
		aiActive: isCustomerListed(settings?.enableAI, user?.customer),
		enableBugHospital: isCustomerListed(settings?.enableBugHospital, user?.customer),
		licag: settings?.licag ?? null,
		helpjuiceSecret: settings?.helpjuiceSecret ?? null,
	};
}

// appTranslator.fromJson(): Core/Applications, as stored in externis.applications / APPS.
export function translateApplications(list, token) {
	return (Array.isArray(list) ? list : [])
		.filter((o) => o?.type && String(o.reference ?? '').trim() !== 'MO')
		.map((o) => {
			const reference = String(o.reference).trim();
			const app = { id: o.id, reference: o.reference, label: o.label, description: o.description, type: o.type, url: o.url ? o.url.replace('[TOKEN]', encodeURIComponent(token ?? '')) : 0, imageUrl: o.imageUrl ?? '', bg: o.type === 3 ? 'bg-ecos-app' : 'bg-ecos-app-full', bgTitle: 'nameBlack', icon: o.type === 3 ? 'fa fa-4x fa-puzzle-piece' : '' };
			if (['SALFO', 'DCRFO', 'DCRBO'].includes(reference)) Object.assign(app, { label: '', url: '#ecos' });
			if (['ASSFO', 'ASSBO', 'PSPFO'].includes(reference)) Object.assign(app, { label: '', imageUrl: `/m.eagle/img/${reference}xs.png` });
			return app;
		});
}

// login.js checkLoginError(): the Auth error codes the login page answers.
const LOGIN_ERRORS = { 'ERR-0092': 'warning', 'ERR-0093': 'warning', 'ERR-0094': 'warning', 'ERR-0134': 'error' };
export const PASSWORD_EXPIRED = 'ERR-0096';

export function loginErrorAction(code) {
	if (code === PASSWORD_EXPIRED) return { panel: 'expired' };
	return LOGIN_ERRORS[code] ? { alert: LOGIN_ERRORS[code] } : null;
}

// home.js autoLoadSALFO(): the AUTOLOAD deep link (eagle.js replayAutoload) of an SSO module.
export function ssoAutoload(module, param, { delegation = false, appointmentType = 2 } = {}) {
	switch (String(module ?? '').toUpperCase()) {
		case 'VISIT': {
			const [uid = '', dlId = ''] = String(param ?? '').split(',');
			const appUid = uid.trim();
			return { autoload: `menuSFA|vw-visit|${JSON.stringify({ readonly: !delegation, appointment: { uid: appUid, type: appointmentType } })}`, autoloadParams: { appUid, dlId: parseInt(dlId.trim(), 10) } };
		}
		case 'ORDER':
			return { autoload: 'menuSALES|vwSalesCatalog|' };
		case 'AGENDA':
			return { autoload: 'menuSFA|vwSFAPlanning|' };
		default:
			return { autoload: 'menuSFA|vwSalesEdito|' };
	}
}

// home.js __initHome(): where an SSO handoff (?a=...) lands once the session is open.
export function ssoDestination({ app, module, param, delegation = false, appointmentType } = {}) {
	switch (String(app ?? '').toUpperCase()) {
		case 'SALFO':
		case 'DCRFO':
			return module ? { route: '/home', ...ssoAutoload(module, param, { delegation, appointmentType }) } : { route: '/home' };
		case 'PSPFO':
			return { psp: true };
		default:
			return { route: '/' };
	}
}
